import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import type { SingleValue } from 'react-select';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Flex, Inline, Pressable, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import EditorPanelIcon from '@atlaskit/icon/core/information';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import UnlockIcon from '@atlaskit/icon/core/lock-unlocked';
import { PopupSelect } from '@atlaskit/select';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ReverseTrialLozengeExperiment } from '@confluence/trial-components';
import { CONTEXT_PATH, FOLDER_VIEW } from '@confluence/named-routes';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import {
	ChangeEditionInlineMessageShareAndRestrict,
	ChangeEditionSectionMessageShareAndRestrict,
} from '@confluence/change-edition/entry-points/ShareAndRestrictDialogUpsells';

import type { GeneralAccessLevel } from '../../../commonTypes';
import { PressableLink } from '../../../../PressableLink';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller';
import type { ContentType } from '../../../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';

import {
	i18n,
	standardSubtextI18n,
	longSubtextCanViewAndEditI18n,
	longSubtextCanViewNotEditI18n,
} from './generalAccessLevelSelectorI18n';

const unlockedIconBackground = xcss({
	height: '32px',
	width: '32px',
	borderRadius: '6px',
	background: token('color.background.accent.gray.subtlest'),
	marginRight: 'space.050',
});

const lockedIconBackground = xcss({
	height: '32px',
	width: '32px',
	borderRadius: '6px',
	background: token('color.background.danger'),
	marginRight: 'space.050',
});

const disabledOption = xcss({
	opacity: '0.6',
});

const optionStyle = xcss({
	color: 'color.text',
	fontWeight: 'font.weight.bold',
	paddingLeft: 'space.075',
});

const subtextStyleForOptions = xcss({
	color: 'color.text.subtlest',
	font: token('font.body.small'),
	whiteSpace: 'pre',
	paddingLeft: 'space.075',
});

const subtextStyle = xcss({
	color: 'color.text.subtlest',
	font: token('font.body.small'),
	whiteSpace: 'pre',
});

const optionStackStyle = xcss({
	paddingLeft: 'space.150',
});

const buttonTextStyle = xcss({
	color: 'color.text',
	font: token('font.body'),
	fontWeight: token('font.weight.bold'),
});

const pressableTextStyle = xcss({
	height: '20px',
	color: 'color.text',
	':hover': {
		color: 'color.text.accent.blue',
	},
	':active': {
		color: 'color.text.accent.blue.bolder',
	},
	font: token('font.body'),
	fontWeight: token('font.weight.bold'),
	display: 'flex',
	alignItems: 'center',
});

const inlineMessageUpsellStyle = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	width: '350px',
});

const sectionMessageUpsellStyle = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	width: '380px',
});

const blueTextStyle = xcss({
	color: 'color.text.accent.blue',
});

export type GeneralAccessLevelSelectorProps = {
	isLockedOut: boolean;
	isDraft: boolean;
	hasInheritedRestrictions: boolean;
	onOpenRestrictedChange: (becomeRestricted: boolean) => void;
	openSpaceAccessDetails: () => void;
	shouldGeneralAccessMenuBeOpen: false | undefined;
	isEditable: boolean;
	hideSpaceOrParentLink?: boolean;
	parentPageLink: string | null | undefined;
	/*
	 * showLongSubtext adds the "can view and edit" or "can view, only some can edit" tags to the end of the subtext
	 * This is currently used in the PublishModal to display the restriction state without using the "Can edit"/"Can view" dropdown.
	 */
	showLongSubtext: boolean;
	generalAccessLevel: GeneralAccessLevel;
	hasRestrictions: boolean;
	hasViewRestrictions: boolean;
	// Used for analytics
	isLivePage: boolean;
	contentType: ContentType;
	edition: ConfluenceEdition;
	handleOpenAccessWarningDialog: () => void;
	shouldShowDeletedGroupDetails: boolean;
	shouldResolveMismatchedRestrictions: boolean;
};

type OptionLabels = {
	open: string;
	openInherited: string;
	restricted: string;
};

type OptionType = SingleValue<
	| {
			label: JSX.Element;
			value: string;
			isDisabled: boolean;
	  }
	| {
			label: JSX.Element;
			value: string;
			isDisabled?: boolean;
	  }
>;

export const GeneralAccessLevelSelector = ({
	isLockedOut,
	isDraft,
	hasInheritedRestrictions,
	onOpenRestrictedChange,
	openSpaceAccessDetails,
	handleOpenAccessWarningDialog,
	shouldGeneralAccessMenuBeOpen,
	parentPageLink,
	isEditable,
	hideSpaceOrParentLink,
	showLongSubtext,
	generalAccessLevel,
	shouldShowDeletedGroupDetails,
	hasRestrictions,
	hasViewRestrictions,
	// Used for analytics
	isLivePage,
	contentType,
	edition,
	shouldResolveMismatchedRestrictions,
}: GeneralAccessLevelSelectorProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const OPTION_LABELS: OptionLabels = {
		open: formatMessage(i18n.openAccessLabel),
		openInherited: formatMessage(i18n.restrictedByParentAccessLabel),
		restricted: formatMessage(i18n.restrictedAccessLabel),
	};

	const isGeneralAccessOpen = generalAccessLevel !== 'restricted';
	const isFreeEdition = edition === ConfluenceEdition.FREE;

	const shouldDisable = isFreeEdition || hasInheritedRestrictions;
	const showOpenAccessUpsell = generalAccessLevel !== 'open-can-edit';

	let parentUrl = `${CONTEXT_PATH}${parentPageLink}`;
	if (FOLDER_VIEW.match(parentUrl)) {
		parentUrl += '?referrer=share-and-restrict-dialog';
	}

	const getLabel = (selectedOption: number) => {
		if (selectedOption === 0) {
			return OPTION_LABELS.open;
		}
		if (selectedOption === 1 && hasInheritedRestrictions) {
			return OPTION_LABELS.openInherited;
		}
		return OPTION_LABELS.restricted;
	};

	const getLabelForMismatchRestrictionsWithQuery = (hasRestrictions: boolean) => {
		if (shouldResolveMismatchedRestrictions || !hasRestrictions) {
			return OPTION_LABELS.open;
		}
		if (hasInheritedRestrictions) {
			return OPTION_LABELS.openInherited;
		}
		return OPTION_LABELS.restricted;
	};

	const subtextI18n = (() => {
		if (!showLongSubtext) return standardSubtextI18n;
		if (generalAccessLevel === 'open-can-edit') {
			return longSubtextCanViewAndEditI18n;
		}
		// else, generalAccessLevel === 'open-can-view' or 'restricted',
		// both of which are handled by `longSubtextCanViewNotEditI18n`.
		return longSubtextCanViewNotEditI18n;
	})();

	const getSubtext = ({
		isOpen,
		hasInheritedRestrictionsSubtext,
	}: {
		isOpen: boolean;
		hasInheritedRestrictionsSubtext: boolean;
	}) => {
		if (shouldShowDeletedGroupDetails) {
			return subtextI18n.onlySpecificPeopleSubtext;
		}
		if (isDraft && hasInheritedRestrictionsSubtext && isOpen) {
			return subtextI18n.draftAnyoneOnTheParentSubtext;
		}
		if (isDraft && !hasInheritedRestrictionsSubtext && isOpen) {
			return subtextI18n.draftAnyoneInThisSpaceSubtext;
		}
		if (isDraft && !isOpen) {
			return subtextI18n.draftOnlySpecificPeopleSubtext;
		}
		if (!isDraft && hasInheritedRestrictionsSubtext && isOpen) {
			return subtextI18n.anyoneOnTheParentSubtext;
		}
		if (!isDraft && !hasInheritedRestrictionsSubtext && isOpen) {
			return subtextI18n.anyoneInThisSpaceSubtext;
		}
		// !isDraft && !isOpen
		return subtextI18n.onlySpecificPeopleSubtext;
	};

	const handleSpaceAccessDetailsClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				actionSubject: 'button',
				actionSubjectId: 'AuditSpaceAccessButton',
				action: 'clicked',
			},
		}).fire();

		openSpaceAccessDetails();
	}, [createAnalyticsEvent, openSpaceAccessDetails]);

	const makeSubtext = ({
		isOpen,
		hasInheritedRestrictionsSubtext,
		forOptions = false,
	}: {
		isOpen: boolean;
		hasInheritedRestrictionsSubtext: boolean;
		forOptions?: boolean;
	}) =>
		formatMessage(getSubtext({ isOpen, hasInheritedRestrictionsSubtext }), {
			a: (chunks: React.ReactNode[]) => {
				if (shouldShowDeletedGroupDetails) {
					return <>{chunks}</>;
				}
				if (forOptions || openSpaceAccessDetails === undefined) {
					return <>{chunks}</>;
				} else if (hasInheritedRestrictionsSubtext) {
					return <></>;
				} else {
					return (
						<PressableLink showUnderline fontSize="small" onClick={handleSpaceAccessDetailsClick}>
							{chunks}
						</PressableLink>
					);
				}
			},
			parentLink: (chunks: React.ReactNode[]) =>
				forOptions ? (
					<>{chunks}</>
				) : (
					<PressableLink targetBlank href={parentUrl} showUnderline fontSize="small">
						{chunks}
					</PressableLink>
				),
		});

	const makeOptions = (): OptionType[] => [
		...(isFreeEdition
			? [
					{
						label: (
							<>
								{showOpenAccessUpsell ? (
									<Box xcss={sectionMessageUpsellStyle}>
										<ChangeEditionSectionMessageShareAndRestrict
											handleOnClick={handleOpenAccessWarningDialog}
											customPadding={`${token('space.200')}`}
										/>
									</Box>
								) : (
									<Box xcss={inlineMessageUpsellStyle}>
										<ChangeEditionInlineMessageShareAndRestrict
											source="generalAccessLevelSelector"
											customPadding={`${token('space.100')} ${token('space.200')} ${token('space.200')} ${token('space.200')}`}
										/>
									</Box>
								)}
							</>
						),
						value: 'tryItFree',
						isDisabled: isFreeEdition,
					},
				]
			: []),
		{
			label: (
				<Inline grow="fill" spread="space-between" alignInline="center">
					<Flex alignItems="center">
						<Flex
							justifyContent="center"
							alignItems="center"
							xcss={[unlockedIconBackground, shouldDisable && disabledOption]}
						>
							<UnlockIcon label={formatMessage(i18n.unlockIconLabel)} color={token('color.icon')} />
						</Flex>
						<Box xcss={shouldDisable && disabledOption}>
							<Box xcss={[optionStyle]}>{OPTION_LABELS.open}</Box>
							<Inline alignBlock="center" xcss={[subtextStyleForOptions]}>
								{makeSubtext({
									isOpen: true,
									hasInheritedRestrictionsSubtext: false,
									forOptions: true,
								})}
							</Inline>
						</Box>
					</Flex>
					{hasInheritedRestrictions && !isFreeEdition && (
						<Tooltip content={formatMessage(i18n.restrictedByParentTooltip)} position="bottom">
							{(tooltipProps) => (
								<IconButton
									{...tooltipProps}
									spacing="compact"
									shape="circle"
									appearance="subtle"
									icon={EditorPanelIcon}
									label={formatMessage(i18n.restrictedByParentTooltipIconLabel)}
								/>
							)}
						</Tooltip>
					)}
				</Inline>
			),
			value: 'open',
			isDisabled: shouldDisable,
		},
		...(hasInheritedRestrictions
			? [
					{
						label: (
							<Flex alignItems="center">
								<Flex
									justifyContent="center"
									alignItems="center"
									xcss={[lockedIconBackground, isFreeEdition && disabledOption]}
								>
									<LockIcon
										label={formatMessage(i18n.lockIconLabel)}
										color={token('color.icon.danger')}
									/>
								</Flex>
								<Box xcss={isFreeEdition && disabledOption}>
									<Box xcss={[optionStyle]}>{OPTION_LABELS.openInherited}</Box>
									<Inline alignBlock="center" xcss={[subtextStyleForOptions]}>
										{makeSubtext({
											isOpen: true,
											hasInheritedRestrictionsSubtext: true,
											forOptions: true,
										})}
									</Inline>
								</Box>
							</Flex>
						),
						value: 'open',
						isDisabled: isFreeEdition,
					},
				]
			: []),
		{
			label: (
				<Flex alignItems="center">
					<Flex
						justifyContent="center"
						alignItems="center"
						xcss={[lockedIconBackground, isFreeEdition && disabledOption]}
					>
						<LockIcon
							label={formatMessage(i18n.lockIconLabel)}
							color={token('color.icon.danger')}
						/>
					</Flex>
					<Box xcss={isFreeEdition && disabledOption}>
						<Inline>
							<Box xcss={[optionStyle]}>{OPTION_LABELS.restricted}</Box>
							<ReverseTrialLozengeExperiment
								touchpointId="confluence_page_restrictions_dialog_list_item"
								edition={ConfluenceEdition.STANDARD}
							/>
						</Inline>
						<Inline alignBlock="center" xcss={[subtextStyleForOptions]}>
							{makeSubtext({
								isOpen: false,
								hasInheritedRestrictionsSubtext: hasInheritedRestrictions,
								forOptions: true,
							})}
						</Inline>
					</Box>
				</Flex>
			),
			value: 'restricted',
			isDisabled: isFreeEdition,
		},
	];

	const handleGeneralAccessChange = (selectedOption: OptionType) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'selected',
				actionSubject: 'option',
				actionSubjectId: 'GeneralAccessLevelSelector',
				attributes: {
					selectedOption: selectedOption?.value,
					hasInheritedRestrictions,
					isDraft,
					isLivePage,
					contentType,
				},
			},
		}).fire();
		onOpenRestrictedChange(selectedOption?.value === 'restricted');
	};

	const getCurrentOptionValue = () => {
		let currentOption: number;

		//Utilizes the restrictions data from singleContent query, which includes unfiltered information regarding deleted groups which hold content restrictions.
		//This will only calculate the current option value if:
		//1. The feature flag is enabled (fg('confluence_frontend_perms_deleted_groups_treatment'))
		//2. The content has mismatching query restrictions (hasMismatchWithQueryRestrictions, defined in the derivedState)
		//See https://jira.atlassian.com/browse/CONFCLOUD-80781 for more context.
		if (shouldResolveMismatchedRestrictions && shouldShowDeletedGroupDetails) {
			return (currentOption = 0);
		}
		if (shouldShowDeletedGroupDetails) {
			hasRestrictions || hasViewRestrictions ? (currentOption = 1) : (currentOption = 0);
			return currentOption;
		}

		if (isGeneralAccessOpen) {
			currentOption = 0;
		} else {
			currentOption = 1;
		}
		// If we have inherited restrictions, option 0 is disabled and a third option
		// (Restricted by a parent) is added (as though spliced at 1), so the available options are 1 and 2
		// instead of 0 and 1, and we can just add 1 to selectedOption to account for this.
		if (hasInheritedRestrictions) {
			currentOption += 1;
		}

		return currentOption;
	};

	const options = makeOptions();

	const currentOption = getCurrentOptionValue();

	const selectedLabel = shouldShowDeletedGroupDetails
		? getLabelForMismatchRestrictionsWithQuery(hasRestrictions)
		: getLabel(currentOption);

	return (
		<Flex alignItems="center">
			{currentOption === 0 ? (
				<Flex justifyContent="center" alignItems="center" xcss={unlockedIconBackground}>
					<UnlockIcon label="unlocked" color={token('color.icon')} />
				</Flex>
			) : (
				<Flex justifyContent="center" alignItems="center" xcss={lockedIconBackground}>
					<LockIcon label="locked" color={token('color.icon.danger')} />
				</Flex>
			)}
			<Stack xcss={optionStackStyle}>
				{isEditable ? (
					<PopupSelect
						options={options}
						value={options[currentOption]}
						spacing="compact"
						id="general-access-level-options"
						target={({ isOpen, ...triggerProps }) => (
							<Pressable
								{...triggerProps}
								isDisabled={isLockedOut}
								padding="space.0"
								backgroundColor="color.background.neutral.subtle"
								xcss={[pressableTextStyle, isOpen && blueTextStyle]}
								aria-describedby="instru"
							>
								<Box>{selectedLabel}</Box>
								<ChevronDownIcon
									spacing="spacious"
									label={formatMessage(i18n.dropdownIconLabel)}
									size="small"
								/>
							</Pressable>
						)}
						onChange={handleGeneralAccessChange}
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
						styles={{
							option: (styles, { data }) => {
								const value = data?.value;
								return {
									...styles,
									color: value === 'tryItFree' ? `${token('color.text')}` : ' ',
									padding:
										value === 'tryItFree'
											? `${token('space.0')}`
											: `${token('space.075')} ${token('space.150')}`,
								};
							},
						}}
						menuIsOpen={shouldGeneralAccessMenuBeOpen}
					/>
				) : (
					<Box xcss={[buttonTextStyle]}>{selectedLabel}</Box>
				)}
				<Box xcss={[subtextStyle]} id="instru">
					{makeSubtext({
						isOpen: isGeneralAccessOpen,
						hasInheritedRestrictionsSubtext: hasInheritedRestrictions,
						forOptions: hideSpaceOrParentLink,
					})}
				</Box>
			</Stack>
		</Flex>
	);
};
